import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Plus, Minus, Download, Send, Info, X } from 'lucide-react';
import { 
  modelSizes, 
  colorCombinations, 
  surfaceSystems,
  upgradeAddons,
  configuratorLogo
} from '../../mock';
import { setupRobotoFont } from '../../fonts/RobotoFont';

// Lazy load jsPDF only when needed (saves ~200KB from initial bundle)
const loadJsPDF = () => import('jspdf').then(module => module.jsPDF);

const Configurator = ({ t, language, onConfigChange, onSendInquiry }) => {
  const [currentModelIndex, setCurrentModelIndex] = useState(0);
  const [selectedSizeIndex, setSelectedSizeIndex] = useState(0);
  const [selectedFlooring, setSelectedFlooring] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [duplexCoating, setDuplexCoating] = useState(false);
  const [showDuplexInfo, setShowDuplexInfo] = useState(false);
  const [addonQuantities, setAddonQuantities] = useState({
    fitness: 0,
    solar_led: 0,
    bench: 0,
    trash: 0
  });
  // Track if user has actively interacted with configurator
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Memoize current model and size to prevent recalculation
  const currentModel = useMemo(() => modelSizes[currentModelIndex], [currentModelIndex]);
  const currentSize = useMemo(() => currentModel.sizes[selectedSizeIndex], [currentModel, selectedSizeIndex]);

  // Translated model display name (RECT uses manual translation override)
  const modelDisplayName = useMemo(
    () => (currentModel.nameKey ? t[currentModel.nameKey] : currentModel.model),
    [currentModel, t]
  );

  // Translated size label ('По поръчка' / 'Custom size' for RECT)
  const sizeLabel = useCallback((s) => (s?.sizeKey ? t[s.sizeKey] : s?.size), [t]);

  // Update addon quantity
  const updateAddonQuantity = useCallback((addonId, delta) => {
    setHasUserInteracted(true);
    setAddonQuantities(prev => ({
      ...prev,
      [addonId]: Math.max(0, prev[addonId] + delta)
    }));
  }, []);

  // Model navigation
  const handleModelSelect = useCallback((idx) => {
    setHasUserInteracted(true);
    setCurrentModelIndex(idx);
    setSelectedSizeIndex(0);
  }, []);

  // Generate configuration summary text based on current language
  const configSummary = useMemo(() => {
    const lines = [];
    
    // Model & Size
    lines.push(`${t.configSummaryModel}: ${modelDisplayName}`);
    lines.push(`${t.configSummarySize}: ${currentSize.dimensions ? `${sizeLabel(currentSize)} (${currentSize.dimensions})` : sizeLabel(currentSize)}`);
    
    // Color
    if (selectedColor) {
      const color = colorCombinations.find(c => c.id === selectedColor);
      if (color) {
        lines.push(`${t.configSummaryColor}: ${t[color.name]}`);
      }
    }
    
    // Duplex Coating
    lines.push(`${t.configSummaryDuplex}: ${duplexCoating ? t.configSummaryYes : t.configSummaryNo}`);
    
    // Flooring
    if (selectedFlooring) {
      const flooring = surfaceSystems.find(f => f.id === selectedFlooring);
      if (flooring) {
        lines.push(`${t.configSummaryFlooring}: ${t[flooring.name]}`);
      }
    } else {
      lines.push(`${t.configSummaryFlooring}: -`);
    }
    
    // Upgrades
    const selectedUpgrades = upgradeAddons
      .filter(addon => addonQuantities[addon.id] > 0)
      .map(addon => `${t[addon.name]} x${addonQuantities[addon.id]}`);
    
    if (selectedUpgrades.length > 0) {
      lines.push(`${t.configSummaryUpgrades}: ${selectedUpgrades.join(', ')}`);
    }
    
    return lines.join('\n');
  }, [t, currentModel, currentSize, selectedColor, selectedFlooring, duplexCoating, addonQuantities]);

  // Emit config summary on every change (reactive update) - ONLY if user has interacted
  useEffect(() => {
    if (onConfigChange) {
      // Only pass summary if user has actually interacted with the configurator
      onConfigChange(hasUserInteracted ? configSummary : '');
    }
  }, [configSummary, onConfigChange, hasUserInteracted]);

  // Handle send inquiry button click
  const handleSendInquiry = useCallback(() => {
    if (onSendInquiry) {
      onSendInquiry();
    }
    // Scroll to contact form
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  }, [onSendInquiry]);

  // Export configuration as PDF with professional branding (lazy loaded)
  const exportConfigurationPDF = useCallback(async () => {
    try {
      const jsPDF = await loadJsPDF();
      const doc = new jsPDF();
      
      // Setup Roboto font for Cyrillic support (synchronous, embedded font)
      const fontLoaded = setupRobotoFont(doc);
      if (!fontLoaded) {
        // Fallback - still try to generate PDF but warn user
        console.warn('Roboto font could not be loaded, Cyrillic may not render correctly');
      }
      
      // Brand colors
      const brandOrange = [233, 75, 15];
      const brandBlack = [15, 15, 16];
      const textGrey = [100, 100, 100];
      
      // PDF Labels based on language
      const pdfLabels = {
        bg: {
          subtitle: 'Вашата конфигурация',
          model: 'ИЗБРАН МОДЕЛ',
          color: 'ЦВЯТ',
          duplex: 'DUPLEX ПОКРИТИЕ',
          duplexYes: 'Да - Клас C4 по ISO 12944',
          duplexNo: 'Не',
          flooring: 'СПОРТНА НАСТИЛКА',
          addons: 'ДОПЪЛНИТЕЛНИ ОПЦИИ'
        },
        en: {
          subtitle: 'Your Configuration',
          model: 'SELECTED MODEL',
          color: 'COLOR',
          duplex: 'DUPLEX COATING',
          duplexYes: 'Yes - C4 Class ISO 12944',
          duplexNo: 'No',
          flooring: 'SPORT FLOORING',
          addons: 'UPGRADE ADD-ONS'
        },
        fr: {
          subtitle: 'Votre Configuration',
          model: 'MODÈLE SÉLECTIONNÉ',
          color: 'COULEUR',
          duplex: 'REVÊTEMENT DUPLEX',
          duplexYes: 'Oui - Classe C4 ISO 12944',
          duplexNo: 'Non',
          flooring: 'REVÊTEMENT SPORTIF',
          addons: 'OPTIONS SUPPLÉMENTAIRES'
        },
        de: {
          subtitle: 'Ihre Konfiguration',
          model: 'AUSGEWÄHLTES MODELL',
          color: 'FARBE',
          duplex: 'DUPLEX-BESCHICHTUNG',
          duplexYes: 'Ja - Klasse C4 ISO 12944',
          duplexNo: 'Nein',
          flooring: 'SPORTBODEN',
          addons: 'ZUSÄTZLICHE OPTIONEN'
        }
      };
      
      const labels = pdfLabels[language] || pdfLabels.en;
      
      // Header background
      doc.setFillColor(...brandBlack);
      doc.rect(0, 0, 210, 50, 'F');
      
      // Header accent line
      doc.setFillColor(...brandOrange);
      doc.rect(0, 50, 210, 3, 'F');
      
      // Logo text in header
      doc.setFontSize(28);
      doc.setTextColor(255, 255, 255);
      doc.text('UrbanCage', 105, 25, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setTextColor(...brandOrange);
      doc.text('\u00AE', 148, 18);
      
      // Subtitle
      doc.setFontSize(11);
      doc.setTextColor(200, 200, 200);
      doc.text(labels.subtitle, 105, 38, { align: 'center' });
      
      // Configuration details section
      let yPos = 70;
      
      // Section: Selected Model
      doc.setFontSize(14);
      doc.setTextColor(...brandOrange);
      doc.text(labels.model, 20, yPos);
      
      yPos += 10;
      doc.setFontSize(20);
      doc.setTextColor(...brandBlack);
      doc.text(`${modelDisplayName}`, 20, yPos);
      
      yPos += 10;
      doc.setFontSize(12);
      doc.setTextColor(...textGrey);
      // Use proper superscript for m²
      doc.text(currentSize.dimensions ? `${sizeLabel(currentSize)} | ${currentSize.dimensions}` : `${sizeLabel(currentSize)}`, 20, yPos);
      
      // Divider
      yPos += 15;
      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.5);
      doc.line(20, yPos, 190, yPos);
      
      // Section: Selected Color
      yPos += 15;
      if (selectedColor) {
        const color = colorCombinations.find(c => c.id === selectedColor);
        
        if (color) {
          doc.setFontSize(14);
          doc.setTextColor(...brandOrange);
          doc.text(labels.color, 20, yPos);
          
          yPos += 10;
          doc.setFontSize(12);
          doc.setTextColor(...brandBlack);
          // Use translated color name from t object
          const colorDisplayName = t[color.name] || color.name;
          doc.text(colorDisplayName, 20, yPos);
          
          // Draw DYNAMIC color preview boxes using actual color values
          const primaryRGB = [
            parseInt(color.colors.primary.slice(1, 3), 16),
            parseInt(color.colors.primary.slice(3, 5), 16),
            parseInt(color.colors.primary.slice(5, 7), 16)
          ];
          const accentRGB = [
            parseInt(color.colors.accent.slice(1, 3), 16),
            parseInt(color.colors.accent.slice(3, 5), 16),
            parseInt(color.colors.accent.slice(5, 7), 16)
          ];
          doc.setFillColor(...primaryRGB);
          doc.rect(150, yPos - 6, 15, 8, 'F');
          doc.setFillColor(...accentRGB);
          doc.rect(168, yPos - 6, 15, 8, 'F');
          
          yPos += 15;
          doc.setDrawColor(230, 230, 230);
          doc.line(20, yPos, 190, yPos);
          yPos += 15;
        }
      }

      // Section: Duplex Coating (always show, with Yes/No)
      doc.setFontSize(14);
      doc.setTextColor(...brandOrange);
      doc.text(labels.duplex, 20, yPos);
      
      yPos += 10;
      doc.setFontSize(12);
      doc.setTextColor(...brandBlack);
      doc.text(duplexCoating ? labels.duplexYes : labels.duplexNo, 20, yPos);
      
      yPos += 15;
      doc.setDrawColor(230, 230, 230);
      doc.line(20, yPos, 190, yPos);
      yPos += 15;
      
      // Section: Sport Flooring
      doc.setFontSize(14);
      doc.setTextColor(...brandOrange);
      doc.text(labels.flooring, 20, yPos);
      
      yPos += 10;
      doc.setFontSize(12);
      doc.setTextColor(...brandBlack);
      if (selectedFlooring) {
        const flooring = surfaceSystems.find(f => f.id === selectedFlooring);
        if (flooring) {
          // Use translated flooring name from t object
          const flooringDisplayName = t[flooring.name] || flooring.name;
          doc.text(flooringDisplayName, 20, yPos);
        } else {
          doc.text('-', 20, yPos);
        }
      } else {
        doc.text('-', 20, yPos);
      }
      
      yPos += 15;
      doc.setDrawColor(230, 230, 230);
      doc.line(20, yPos, 190, yPos);
      yPos += 15;
    
      // Section: Upgrades (ALL selected add-ons) - Dynamic positioning
      const selectedAddons = upgradeAddons.filter(addon => addonQuantities[addon.id] > 0);
      if (selectedAddons.length > 0) {
        // Footer starts at y=260, so content must end before ~250
        const maxContentY = 250;
        const addonLineHeight = 8;
        const headerHeight = 10;
        
        // If the header itself won't fit before the footer, move to a new page
        if (yPos + headerHeight + addonLineHeight > maxContentY) {
          doc.addPage();
          yPos = 30;
        }
        
        doc.setFontSize(14);
        doc.setTextColor(...brandOrange);
        doc.text(labels.addons, 20, yPos);
        
        yPos += headerHeight;
        
        doc.setFontSize(11);
        doc.setTextColor(...brandBlack);
        
        // List ALL selected addons; overflow to a new page when space runs out
        selectedAddons.forEach(addon => {
          if (yPos > maxContentY) {
            doc.addPage();
            yPos = 30;
            doc.setFontSize(11);
            doc.setTextColor(...brandBlack);
          }
          
          const qty = addonQuantities[addon.id];
          // Use translated addon name from t object
          const addonDisplayName = t[addon.name] || addon.name;
          doc.setFillColor(...brandOrange);
          doc.circle(23, yPos - 2, 1.5, 'F');
          doc.text(addonDisplayName, 28, yPos);
          doc.setTextColor(...textGrey);
          doc.text(`x${qty}`, 180, yPos);
          doc.setTextColor(...brandBlack);
          yPos += addonLineHeight;
        });
      }
    
      // Footer background
      doc.setFillColor(...brandBlack);
      doc.rect(0, 260, 210, 37, 'F');
      
      // Footer accent line
      doc.setFillColor(...brandOrange);
      doc.rect(0, 260, 210, 2, 'F');
      
      // Footer content
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text('info@urbancage.bg', 105, 272, { align: 'center' });
      doc.text('+359 888 360 339', 105, 280, { align: 'center' });
      
      doc.setTextColor(...brandOrange);
      doc.text('urbancage.bg', 105, 288, { align: 'center' });
      
      // Generate filename and save
      const filename = `UrbanCage-${currentModel.model}-${currentSize.size.replace(/\s/g, '')}.pdf`;
      doc.save(filename);
      
    } catch (error) {
      alert('Error generating PDF: ' + error.message);
    }
  }, [currentModel, currentSize, selectedColor, selectedFlooring, duplexCoating, addonQuantities, t, language, modelDisplayName]);

  return (
    <section id="models" className="configurator-section" aria-labelledby="configurator-title">
      <h2 id="configurator-title" className="section-title configurator-title">
        <span className="title-brand">UrbanCage<sup className="trademark">{'\u00AE'}</sup></span>
        <span className="configurator-word">
          {language === 'bg' ? ' конфигуратор' : language === 'en' ? ' Configurator' : language === 'fr' ? ' Configurateur' : ' Konfigurator'}
        </span>
      </h2>
      <p className="section-subtitle">{t.configuratorSubtitle}</p>
      
      {/* Step 1: Model Selection Cards - Horizontal Scroll */}
      <div className="config-step">
        <h3 className="config-step-label">
          {language === 'bg' ? 'Стъпка 1: Изберете модел' : language === 'fr' ? '\u00C9tape 1 : Choisissez un mod\u00E8le' : language === 'de' ? 'Schritt 1: Modell w\u00E4hlen' : 'Step 1: Choose a model'}
        </h3>
        <div className="model-cards-slider">
          <div className="model-cards-container">
            {modelSizes.map((model, idx) => {
              const displayName = model.nameKey ? t[model.nameKey] : model.model;
              return (
                <button
                  key={`model-${model.model}`}
                  className={`model-card ${idx === currentModelIndex ? 'active' : ''}`}
                  onClick={() => handleModelSelect(idx)}
                  data-testid={`model-card-${model.model}`}
                  aria-pressed={idx === currentModelIndex}
                >
                  <span className="drawing-frame model-card-frame">
                    <img 
                      src={model.sizes[0].image} 
                      alt={language === 'bg' ? `UrbanCage ${displayName} модулно мултиспорт игрище` : `UrbanCage ${displayName} modular multi-sport court`} 
                      className={`model-card-img${model.sizes[0].overlayLogo ? ' with-logo-inset' : ''}`}
                      width="300"
                      height="200"
                    />
                    {model.sizes[0].overlayLogo && (
                      <img src={configuratorLogo} alt="" aria-hidden="true" className="drawing-logo" />
                    )}
                  </span>
                  <span className="model-card-name">{displayName}</span>
                  {model.areaRange && <span className="model-card-area">{model.areaRange}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Step 2: Size Selection */}
      <div className="config-step">
        <h3 className="config-step-label">
          {language === 'bg' ? 'Стъпка 2: Изберете размер' : language === 'fr' ? '\u00C9tape 2 : Choisissez une taille' : language === 'de' ? 'Schritt 2: Gr\u00F6\u00DFe w\u00E4hlen' : 'Step 2: Choose a size'}
        </h3>
        <div className="config-step-panel">
          <div className="size-preview">
            <div className="drawing-frame preview-frame" data-testid="size-preview-frame">
              <img 
                src={currentSize?.image || currentModel.sizes[0].image} 
                alt={`${modelDisplayName} - ${sizeLabel(currentSize)}`} 
                className={`model-main-image${currentSize?.overlayLogo ? ' with-logo-inset' : ''}`} 
              />
              {currentSize?.overlayLogo && (
                <img src={configuratorLogo} alt="" aria-hidden="true" className="drawing-logo" data-testid="preview-logo-overlay" />
              )}
            </div>
          </div>
          <div className="size-controls">
            <div className="size-cards" data-testid="size-cards-list">
              {currentModel.sizes.map((sizeObj, idx) => (
                <button
                  key={`size-${currentModel.model}-${sizeObj.variant}`}
                  className={`size-card ${idx === selectedSizeIndex ? 'active' : ''}`}
                  onClick={() => { setHasUserInteracted(true); setSelectedSizeIndex(idx); }}
                  data-testid={`size-card-${sizeObj.variant}`}
                  aria-pressed={idx === selectedSizeIndex}
                >
                  <span className="drawing-frame thumb-frame">
                    <img 
                      src={sizeObj.image} 
                      alt="" 
                      className={`size-card-thumb${sizeObj.overlayLogo ? ' with-logo-inset' : ''}`} 
                      width="128" 
                      height="80" 
                      loading="lazy"
                    />
                    {sizeObj.overlayLogo && (
                      <img src={configuratorLogo} alt="" aria-hidden="true" className="drawing-logo" />
                    )}
                  </span>
                  <span className="size-card-info">
                    {!currentModel.nameKey && <span className="size-card-variant">{sizeObj.variant}</span>}
                    <span className="size-card-name">{sizeLabel(sizeObj)}</span>
                    {sizeObj.dimensions && <span className="size-card-dim">{sizeObj.dimensions}</span>}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Step 3: UPGRADE Add-ons - Horizontal Layout */}
      <div className="config-step">
        <h3 className="config-step-label">
          {language === 'bg' ? 'Стъпка 3: Добавки' : language === 'fr' ? '\u00C9tape 3 : Options' : language === 'de' ? 'Schritt 3: Optionen' : 'Step 3: Options'}
        </h3>
        <div className="config-option-section upgrade-section-horizontal">
          <h4 className="config-option-title">{t.upgradesTitle}</h4>
          <p className="config-option-subtitle">{t.upgradesSubtitle}</p>
          <div className="upgrade-options-horizontal">
            {upgradeAddons.map((addon) => (
              <div key={`addon-${addon.id}`} className="upgrade-option-card">
                <span className="upgrade-name">{t[addon.name]}</span>
                <div className="quantity-selector">
                  <button 
                    className="qty-btn minus"
                    onClick={() => updateAddonQuantity(addon.id, -1)}
                    disabled={addonQuantities[addon.id] === 0}
                    aria-label={`Decrease ${t[addon.name]} quantity`}
                  >
                    <Minus size={16} />
                  </button>
                  <span className="qty-value" aria-live="polite">{addonQuantities[addon.id]}</span>
                  <button 
                    className="qty-btn plus"
                    onClick={() => updateAddonQuantity(addon.id, 1)}
                    aria-label={`Increase ${t[addon.name]} quantity`}
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Step 4: UrbanCage SELECT (Color + Duplex + Flooring) */}
      <div className="config-step">
        <h3 className="config-step-label">
          {language === 'bg' ? 'Стъпка 4: UrbanCage\u00AE SELECT' : language === 'fr' ? '\u00C9tape 4 : UrbanCage\u00AE SELECT' : language === 'de' ? 'Schritt 4: UrbanCage\u00AE SELECT' : 'Step 4: UrbanCage\u00AE SELECT'}
        </h3>
        <div className="config-step-select-block">
          <div className="step4-header">
            <h4 className="step4-title">
              <span className="title-orange">UrbanCage<sup>{'\u00AE'}</sup></span> <span className="title-white">SELECT</span>
            </h4>
          </div>
        
          {/* Color Selection */}
          <div className="select-step-subsection">
            <h4 className="select-step-title">{t.visualIdentity}</h4>
            <p className="select-step-subtitle">{t.qualityStatement}</p>
            <div className="select-color-grid" role="radiogroup" aria-label={t.visualIdentity}>
              {colorCombinations.map((combo) => (
                <button
                  key={`color-${combo.id}`}
                  className={`select-color-card ${selectedColor === combo.id ? 'selected' : ''}`}
                  onClick={() => { setHasUserInteracted(true); setSelectedColor(selectedColor === combo.id ? null : combo.id); }}
                  data-testid={`color-card-${combo.id}`}
                  role="radio"
                  aria-checked={selectedColor === combo.id}
                >
                  <div className="select-color-preview">
                    <div className="select-color-box" style={{ backgroundColor: combo.colors.primary }}></div>
                    <div className="select-color-box accent" style={{ backgroundColor: combo.colors.accent }}></div>
                  </div>
                  <h5>{t[combo.name]}</h5>
                  <p className="select-color-desc">{t[`${combo.name}Desc`]}</p>
                  {selectedColor === combo.id && (
                    <div className="select-checkmark" aria-hidden="true">{'\u2713'}</div>
                  )}
                </button>
              ))}
            </div>
            <p className="custom-color-note">{t.customColorNote}</p>
          </div>

          {/* Duplex Coating Toggle */}
          <div className="select-step-subsection duplex-section">
            <div className="duplex-toggle-container">
              <label className="duplex-toggle">
                <input
                  type="checkbox"
                  checked={duplexCoating}
                  onChange={(e) => { setHasUserInteracted(true); setDuplexCoating(e.target.checked); }}
                  data-testid="duplex-coating-toggle"
                />
                <span className="duplex-toggle-slider"></span>
                <span className="duplex-toggle-label">{t.duplexCoating}</span>
              </label>
              <button 
                className="duplex-info-btn"
                onClick={() => setShowDuplexInfo(!showDuplexInfo)}
                aria-label="More information about Duplex coating"
                data-testid="duplex-info-btn"
              >
                <Info size={18} />
              </button>
            </div>
            {showDuplexInfo && (
              <div className="duplex-info-popup" data-testid="duplex-info-popup">
                <button 
                  className="duplex-info-close" 
                  onClick={() => setShowDuplexInfo(false)}
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
                <p>{t.duplexCoatingInfo}</p>
              </div>
            )}
          </div>
        
          {/* Sport Flooring Selection - Horizontal Slider */}
          <div className="select-step-subsection">
            <h4 className="select-step-title">{t.flooringTitle}</h4>
            <p className="select-step-subtitle">{t.flooringSubtitle}</p>
            <div className="flooring-slider-container">
              <div className="flooring-slider" role="radiogroup" aria-label={t.flooringTitle}>
                {surfaceSystems.map((surface) => (
                  <button
                    key={`flooring-${surface.id}`}
                    className={`select-flooring-card ${selectedFlooring === surface.id ? 'selected' : ''}`}
                    onClick={() => { setHasUserInteracted(true); setSelectedFlooring(selectedFlooring === surface.id ? null : surface.id); }}
                    data-testid={`flooring-card-${surface.id}`}
                    role="radio"
                    aria-checked={selectedFlooring === surface.id}
                  >
                    <div className="select-flooring-image">
                      <img src={surface.image} alt={t[surface.name]} />
                    </div>
                    <h5>{t[surface.name]}</h5>
                    <p className="select-flooring-desc">{t[surface.description]}</p>
                    {selectedFlooring === surface.id && (
                      <div className="select-checkmark" aria-hidden="true">{'\u2713'}</div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Export & Send Inquiry Buttons */}
      <div className="configurator-footer">
        <button 
          className="export-config-btn"
          onClick={exportConfigurationPDF}
          data-testid="download-pdf-btn"
          type="button"
          aria-label={t.exportConfig}
        >
          <Download size={20} />
          {t.exportConfig}
        </button>
        <button 
          className="send-inquiry-btn"
          onClick={handleSendInquiry}
          data-testid="send-inquiry-btn"
          type="button"
          aria-label={t.sendInquiryBtn}
        >
          <Send size={20} />
          {t.sendInquiryBtn}
        </button>
      </div>
    </section>
  );
};

export default React.memo(Configurator);

